<template>
  <div class="bg-white rounded-md border border-gray-200 overflow-hidden">
    <div class="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
      <h2 class="m-0 p-0 text-base font-semibold text-gray-900 shrink-0">Active Maintenance Windows</h2>
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search windows or points"
        class="flex-1 min-w-0 rounded-md border-gray-300 bg-white px-3 py-2 text-sm transition-colors focus:border-blue-500 focus:ring-blue-500"
      />
      <button
        type="button"
        class="shrink-0 inline-flex min-w-20 sm:min-w-40 items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
        @click="clearSearch"
      >
        Clear
      </button>
      <button
        type="button"
        class="shrink-0 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
        @click="toggleAllExpanded"
      >
        {{ allExpanded ? 'Collapse all' : 'Expand all' }}
      </button>
      <span class="shrink-0 inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
        {{ filteredGroups.length }} {{ filteredGroups.length === 1 ? 'window' : 'windows' }}
      </span>
    </div>

    <div v-if="loading" class="flex min-h-[12rem] items-center justify-center p-8">
      <div class="flex animate-pulse flex-col items-center justify-center space-y-0.5">
        <span class="lnr icon-hourglass !text-4xl !text-blue-500 mb-2 inline-block w-8 text-center"></span>
        <span class="text-base font-medium">Loading Data</span>
        <span class="text-sm text-gray-500">Just a moment…</span>
      </div>
    </div>

    <div v-else-if="filteredGroups.length === 0" class="flex min-h-[12rem] items-center justify-center p-8">
      <div class="flex flex-col items-center justify-center space-y-0.5">
        <span class="lnr icon-inbox !text-4xl !text-gray-500 mb-2 inline-block w-8 text-center"></span>
        <span class="text-base font-medium">{{ searchQuery.trim() ? 'No matching windows' : 'No active maintenance windows' }}</span>
        <span class="text-sm text-gray-500">{{ searchQuery.trim() ? 'Try a different search term.' : 'Points placed in maintenance will appear here.' }}</span>
      </div>
    </div>

    <table v-else class="hidden sm:table w-full text-sm">
      <thead class="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
        <tr>
          <th class="text-left font-medium px-4 py-3 w-8"></th>
          <th class="text-left font-medium px-4 py-3">Window</th>
          <th class="text-left font-medium px-4 py-3">Expires</th>
          <th class="text-left font-medium px-4 py-3">Points</th>
          <th class="text-left font-medium px-4 py-3">Started</th>
          <th class="text-right font-medium px-4 py-3"></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="group in filteredGroups" :key="group.key">
          <tr class="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" @click="toggleExpand(group.key)">
            <td class="px-4 py-3 text-gray-500">
              <button
                type="button"
                :aria-label="isGroupExpanded(group.key) ? 'Collapse' : 'Expand'"
                @click.stop="toggleExpand(group.key)"
              >
                <svg v-if="isGroupExpanded(group.key)" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M6 9l6 6l6 -6" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M9 6l6 6l-6 6" />
                </svg>
              </button>
            </td>
            <td class="px-4 py-3">
              <span class="font-medium text-gray-900">{{ group.label || '—' }}</span>
              <span v-if="group.comment" class="block text-xs text-gray-500 truncate max-w-[16rem]" :title="group.comment">{{ group.comment }}</span>
            </td>
            <td class="px-4 py-3 text-gray-900">
              {{ formatExpiration(group.snooze_until) }}
              <span v-if="remainingLabel(group.snooze_until)" class="block text-xs text-gray-500">
                in {{ remainingLabel(group.snooze_until).replace(/^ — /, '') }}
              </span>
            </td>
            <td class="px-4 py-3">
              <span class="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                {{ group.meter_count }} {{ group.meter_count === 1 ? 'point' : 'points' }}
              </span>
            </td>
            <td class="px-4 py-3 text-gray-700">
              {{ formatStartedAt(group.started_at) }}
              <span v-if="group.initiated_by" class="block text-xs text-gray-500">by {{ group.initiated_by }}</span>
            </td>
            <td class="px-4 py-3 text-right">
              <button
                type="button"
                class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                :disabled="disablingGroup === group.key"
                @click.stop="confirmDisableGroup(group, group.key)"
              >
                {{ disablingGroup === group.key ? 'Disabling…' : 'Disable all' }}
              </button>
            </td>
          </tr>
          <tr v-if="isGroupExpanded(group.key)" class="border-t border-gray-100">
            <td colspan="6" class="p-0">
              <div
                v-if="selectionCount(group.key) > 0"
                class="flex items-center justify-between gap-3 px-4 h-14 border-b border-gray-100 bg-blue-50"
              >
                <span class="text-sm font-medium text-blue-900">
                  {{ selectionCount(group.key) }} {{ selectionCount(group.key) === 1 ? 'point' : 'points' }} selected
                </span>
                <button
                  type="button"
                  class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                  :disabled="disablingSelection === group.key"
                  @click.stop="confirmDisableSelection(group.key)"
                >
                  {{ disablingSelection === group.key ? 'Disabling…' : 'Disable selected' }}
                </button>
              </div>
              <ul class="m-0 p-0 divide-y divide-gray-100">
                <template v-for="(area, areaIdx) in groupTree(group)" :key="'a-' + group.key + '-' + area.name">
                  <li :class="areaIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                    <div
                      class="flex items-center gap-2 px-4 py-3 pl-10 font-medium text-gray-900 hover:bg-gray-100 cursor-pointer select-none"
                      role="button"
                      :aria-expanded="isAreaExpanded(group.key, area)"
                      @click="toggleAreaExpanded(group.key, area)"
                    >
                      <span class="text-gray-400">
                        <svg v-if="isAreaExpanded(group.key, area)" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                          <path d="M6 9l6 6l6 -6" />
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                          <path d="M9 6l6 6l-6 6" />
                        </svg>
                      </span>
                      <label class="m-0 inline-flex items-center gap-2 cursor-pointer" @click.stop>
                        <input
                          type="checkbox"
                          class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          :checked="areaState(group.key, area) === 'all'"
                          :indeterminate.prop="areaState(group.key, area) === 'some'"
                          @change="toggleArea(group.key, area, $event.target.checked)"
                        />
                        {{ area.name }}
                        <span class="text-xs font-normal text-gray-500">({{ area.meterCount }})</span>
                      </label>
                    </div>
                  </li>

                  <template v-if="isAreaExpanded(group.key, area)">
                    <template v-for="(zone, zoneIdx) in area.zones" :key="'z-' + group.key + '-' + area.name + '-' + zone.name">
                      <li :class="zoneIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                        <div
                          class="flex items-center gap-2 px-4 py-3 pl-20 text-gray-800 hover:bg-gray-100 cursor-pointer select-none"
                          role="button"
                          :aria-expanded="isZoneExpanded(group.key, area, zone)"
                          @click="toggleZoneExpanded(group.key, area, zone)"
                        >
                          <span class="text-gray-400">
                            <svg v-if="isZoneExpanded(group.key, area, zone)" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                              <path d="M6 9l6 6l6 -6" />
                            </svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                              <path d="M9 6l6 6l-6 6" />
                            </svg>
                          </span>
                          <label class="m-0 inline-flex items-center gap-2 cursor-pointer" @click.stop>
                            <input
                              type="checkbox"
                              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              :checked="zoneState(group.key, zone) === 'all'"
                              :indeterminate.prop="zoneState(group.key, zone) === 'some'"
                              @change="toggleZone(group.key, zone, $event.target.checked)"
                            />
                            {{ zone.name }}
                            <span class="text-xs text-gray-500">({{ zone.meters.length }})</span>
                          </label>
                        </div>
                      </li>

                      <template v-if="isZoneExpanded(group.key, area, zone)">
                        <li
                          v-for="(meter, meterIdx) in zone.meters"
                          :key="'m-' + group.key + '-' + meter.id"
                          :class="meterIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                        >
                          <label class="m-0 flex items-center gap-2 px-4 py-3 pl-32 cursor-pointer text-sm text-gray-700 hover:bg-gray-100">
                            <input
                              type="checkbox"
                              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              :checked="isSelected(group.key, meter.id)"
                              @change="toggleSelection(group.key, meter.id, $event.target.checked)"
                            />
                            {{ meter.name }}
                          </label>
                        </li>
                      </template>
                    </template>
                  </template>
                </template>
              </ul>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <!-- Mobile stacked view -->
    <ul v-if="!loading && filteredGroups.length > 0" class="sm:hidden m-0 p-0 divide-y divide-gray-100">
      <template v-for="group in filteredGroups" :key="'mobile-' + group.key">
        <li class="w-full">
          <div
            class="px-4 py-3 cursor-pointer select-none hover:bg-gray-50"
            role="button"
            :aria-expanded="isGroupExpanded(group.key)"
            @click="toggleExpand(group.key)"
          >
            <div class="flex items-center gap-2">
              <!-- Chevron centered against the full row height -->
              <span class="text-gray-400 shrink-0 self-center">
                <svg v-if="isGroupExpanded(group.key)" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M6 9l6 6l6 -6" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M9 6l6 6l-6 6" />
                </svg>
              </span>
              <!-- Label + metadata -->
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium text-gray-900 truncate">{{ group.label || formatExpiration(group.snooze_until) }}</span>
                  <span class="shrink-0 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                    {{ group.meter_count }} {{ group.meter_count === 1 ? 'point' : 'points' }}
                  </span>
                </div>
                <div class="text-xs text-gray-500 flex flex-col mt-0.5">
                  <span>{{ formatExpiration(group.snooze_until) }}<template v-if="remainingLabel(group.snooze_until)"> &middot; {{ remainingLabel(group.snooze_until).replace(/^ — /, '') }}</template></span>
                  <span v-if="group.initiated_by">by {{ group.initiated_by }}</span>
                </div>
              </div>
              <!-- Disable button -->
              <button
                type="button"
                class="shrink-0 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                :disabled="disablingGroup === group.key"
                @click.stop="confirmDisableGroup(group, group.key)"
              >
                {{ disablingGroup === group.key ? 'Disabling…' : 'Disable all' }}
              </button>
            </div>
          </div>

          <div v-if="isGroupExpanded(group.key)" class="border-t border-gray-100">
            <div
              v-if="selectionCount(group.key) > 0"
              class="flex items-center justify-between gap-3 px-4 h-14 border-b border-gray-100 bg-blue-50"
            >
              <span class="text-sm font-medium text-blue-900">
                {{ selectionCount(group.key) }} {{ selectionCount(group.key) === 1 ? 'point' : 'points' }} selected
              </span>
              <button
                type="button"
                class="shrink-0 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                :disabled="disablingSelection === group.key"
                @click.stop="confirmDisableSelection(group.key)"
              >
                {{ disablingSelection === group.key ? 'Disabling…' : 'Disable selected' }}
              </button>
            </div>
            <ul class="m-0 p-0 divide-y divide-gray-100">
              <template v-for="(area, areaIdx) in groupTree(group)" :key="'ma-' + group.key + '-' + area.name">
                <li :class="areaIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                  <div
                    class="flex items-center gap-2 pr-4 py-4 pl-2 font-medium text-gray-900 hover:bg-gray-100 cursor-pointer select-none"
                    role="button"
                    :aria-expanded="isAreaExpanded(group.key, area)"
                    @click="toggleAreaExpanded(group.key, area)"
                  >
                    <span class="text-gray-400">
                      <svg v-if="isAreaExpanded(group.key, area)" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <path d="M6 9l6 6l6 -6" />
                      </svg>
                      <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <path d="M9 6l6 6l-6 6" />
                      </svg>
                    </span>
                    <label class="m-0 inline-flex items-center gap-2 cursor-pointer" @click.stop>
                      <input
                        type="checkbox"
                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        :checked="areaState(group.key, area) === 'all'"
                        :indeterminate.prop="areaState(group.key, area) === 'some'"
                        @change="toggleArea(group.key, area, $event.target.checked)"
                      />
                      {{ area.name }}
                      <span class="text-xs font-normal text-gray-500">({{ area.meterCount }})</span>
                    </label>
                  </div>
                </li>

                <template v-if="isAreaExpanded(group.key, area)">
                  <template v-for="(zone, zoneIdx) in area.zones" :key="'mz-' + group.key + '-' + area.name + '-' + zone.name">
                    <li :class="zoneIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                      <div
                        class="flex items-center gap-2 pr-4 py-4 pl-4 text-gray-800 hover:bg-gray-100 cursor-pointer select-none"
                        role="button"
                        :aria-expanded="isZoneExpanded(group.key, area, zone)"
                        @click="toggleZoneExpanded(group.key, area, zone)"
                      >
                        <span class="text-gray-400">
                          <svg v-if="isZoneExpanded(group.key, area, zone)" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                            <path d="M6 9l6 6l6 -6" />
                          </svg>
                          <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                            <path d="M9 6l6 6l-6 6" />
                          </svg>
                        </span>
                        <label class="m-0 inline-flex items-center gap-2 cursor-pointer" @click.stop>
                          <input
                            type="checkbox"
                            class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            :checked="zoneState(group.key, zone) === 'all'"
                            :indeterminate.prop="zoneState(group.key, zone) === 'some'"
                            @change="toggleZone(group.key, zone, $event.target.checked)"
                          />
                          {{ zone.name }}
                          <span class="text-xs text-gray-500">({{ zone.meters.length }})</span>
                        </label>
                      </div>
                    </li>

                    <template v-if="isZoneExpanded(group.key, area, zone)">
                      <li
                        v-for="(meter, meterIdx) in zone.meters"
                        :key="'mm-' + group.key + '-' + meter.id"
                        :class="meterIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                      >
                        <label class="m-0 flex items-center gap-2 pr-4 py-4 pl-6 cursor-pointer text-sm text-gray-700 hover:bg-gray-100">
                          <span class="w-4 shrink-0"></span>
                          <input
                            type="checkbox"
                            class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            :checked="isSelected(group.key, meter.id)"
                            @change="toggleSelection(group.key, meter.id, $event.target.checked)"
                          />
                          {{ meter.name }}
                        </label>
                      </li>
                    </template>
                  </template>
                </template>
              </template>
            </ul>
          </div>
        </li>
      </template>
    </ul>

    <app-modal
      :open="!!pendingDisable"
      title="Confirm disable"
      primary-label="Disable"
      primary-variant="danger"
      :primary-disabled="!disableReason.trim()"
      v-on:submit="executeDisable"
      v-on:cancel="cancelDisable"
      v-on:close="cancelDisable"
    >
      <p v-if="pendingDisable" class="text-sm text-gray-700">
        <template v-if="pendingDisable.type === 'group'">
          You're about to disable maintenance for all
          <strong>{{ pendingDisable.group.meter_count }}</strong>
          {{ pendingDisable.group.meter_count === 1 ? 'point' : 'points' }} in this window.
          Normal alarming will resume immediately.
        </template>
        <template v-else>
          You're about to disable maintenance for the
          <strong>{{ selectionCount(pendingDisable.key) }}</strong> selected
          {{ selectionCount(pendingDisable.key) === 1 ? 'point' : 'points' }}.
          Normal alarming will resume immediately.
        </template>
      </p>
      <div class="mt-4 text-left">
        <label class="block text-sm font-medium text-gray-700 mb-1" for="disable-reason">
          Reason for ending maintenance <span class="text-red-500">*</span>
        </label>
        <textarea
          id="disable-reason"
          v-model="disableReason"
          rows="3"
          maxlength="500"
          placeholder="Describe why maintenance is ending..."
          class="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        ></textarea>
      </div>
      <div class="mt-4 flex items-center gap-2">
        <toggle v-model:value="sendNotification"></toggle>
        <span class="text-sm text-gray-700">Send notification to users</span>
      </div>
    </app-modal>
  </div>
</template>

<script>
import axios from "axios";
import Toggle from "@/components/elements/toggle";
import AppModal from "@/components/elements/AppModal";

export default {
  name: "ActiveMaintenanceWindows",
  components: { Toggle, AppModal },
  props: {
    companyId: { type: String, required: true },
    activeGroups: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    shouldNotifyDefault: { type: Boolean, default: true },
  },
  emits: ["disabled"],
  data() {
    return {
      expandedGroup: null,
      disablingGroup: null,
      selectedByWindow: {},
      disablingSelection: null,
      expandedAreas: new Set(),
      expandedZones: new Set(),
      collapsedAreas: new Set(),
      collapsedZones: new Set(),
      pendingDisable: null,
      disableReason: '',
      sendNotification: this.shouldNotifyDefault,
      searchQuery: '',
      allExpanded: false,
    };
  },
  computed: {
    filteredGroups() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.activeGroups;
      const result = [];
      for (const group of this.activeGroups) {
        const labelMatch = (group.label && group.label.toLowerCase().includes(q)) ||
                           (group.comment && group.comment.toLowerCase().includes(q));
        if (labelMatch) {
          result.push(group);
          continue;
        }
        const matchingMeters = (group.meters ?? []).filter((m) => m.name.toLowerCase().includes(q));
        if (matchingMeters.length > 0) {
          result.push({ ...group, meters: matchingMeters, meter_count: matchingMeters.length });
        }
      }
      return result;
    },
  },
  watch: {
    searchQuery() {
      this.selectedByWindow = {};
    },
  },
  methods: {
    formatExpiration(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      const today = new Date();
      const sameDay = d.toDateString() === today.toDateString();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const isTomorrow = d.toDateString() === tomorrow.toDateString();
      const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
      if (sameDay) return `Today ${time}`;
      if (isTomorrow) return `Tomorrow ${time}`;
      return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    },
    formatStartedAt(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      const today = new Date();
      const sameDay = d.toDateString() === today.toDateString();
      const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
      if (sameDay) return time;
      return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    },
    remainingLabel(iso) {
      if (!iso) return "";
      const ms = new Date(iso).getTime() - Date.now();
      if (ms <= 0) return "";
      const hours = Math.floor(ms / 3_600_000);
      const minutes = Math.floor((ms % 3_600_000) / 60_000);
      return ` — ${hours}h ${minutes}m left`;
    },
    confirmDisableGroup(group, key) {
      this.pendingDisable = { type: "group", group, key };
    },
    confirmDisableSelection(key) {
      this.pendingDisable = { type: "selection", key };
    },
    cancelDisable() {
      this.pendingDisable = null;
      this.disableReason = '';
      this.sendNotification = this.shouldNotifyDefault;
    },
    executeDisable() {
      const pending = this.pendingDisable;
      const reason = this.disableReason;
      this.pendingDisable = null;
      this.disableReason = '';
      if (pending.type === "group") {
        this.disableGroup(pending.group, pending.key, reason);
      } else {
        this.disableSelection(pending.key, reason);
      }
    },
    disableGroup(group, key, comment) {
      this.disablingGroup = key;
      axios
        .post(`/api-internal/${this.companyId}/maintenance/bulk`, {
          action: "disable",
          meter_ids: group.meter_ids,
          comment,
          send_notification: this.sendNotification,
        })
        .then(() => {
          this.$emit("disabled");
        })
        .catch((e) => {
          const msg = e?.response?.data?.error ?? "Failed to disable maintenance window.";
          alert(msg);
        })
        .finally(() => {
          this.disablingGroup = null;
        });
    },
    isSelected(key, meterId) {
      const set = this.selectedByWindow[key];
      return set ? set.has(meterId) : false;
    },
    toggleSelection(key, meterId, checked) {
      const next = { ...this.selectedByWindow };
      const current = next[key] ? new Set(next[key]) : new Set();
      if (checked) current.add(meterId); else current.delete(meterId);
      if (current.size === 0) {
        delete next[key];
      } else {
        next[key] = current;
      }
      this.selectedByWindow = next;
    },
    selectionCount(key) {
      const set = this.selectedByWindow[key];
      return set ? set.size : 0;
    },
    disableSelection(key, comment) {
      const set = this.selectedByWindow[key];
      if (!set || set.size === 0) return;
      this.disablingSelection = key;
      axios
        .post(`/api-internal/${this.companyId}/maintenance/bulk`, {
          action: "disable",
          meter_ids: Array.from(set),
          comment,
          send_notification: this.sendNotification,
        })
        .then(() => {
          // Clear this window's selection; parent refetch will rebuild groups.
          const next = { ...this.selectedByWindow };
          delete next[key];
          this.selectedByWindow = next;
          this.$emit("disabled");
        })
        .catch((e) => {
          const msg = e?.response?.data?.error ?? "Failed to disable selected points.";
          alert(msg);
        })
        .finally(() => {
          this.disablingSelection = null;
        });
    },
    areaExpansionKey(windowKey, areaName) {
      return `${windowKey}::${areaName}`;
    },
    zoneExpansionKey(windowKey, areaName, zoneName) {
      return `${windowKey}::${areaName}::${zoneName}`;
    },
    isAreaExpanded(windowKey, area) {
      if (this.allExpanded) return true;
      if (this.searchQuery.trim()) return true;
      const k = this.areaExpansionKey(windowKey, area.name);
      if (area.zones.length === 1) return !this.collapsedAreas.has(k);
      return this.expandedAreas.has(k);
    },
    isZoneExpanded(windowKey, area, zone) {
      if (this.allExpanded) return true;
      if (this.searchQuery.trim()) return true;
      const k = this.zoneExpansionKey(windowKey, area.name, zone.name);
      if (zone.meters.length === 1) return !this.collapsedZones.has(k);
      return this.expandedZones.has(k);
    },
    toggleAreaExpanded(windowKey, area) {
      const k = this.areaExpansionKey(windowKey, area.name);
      if (area.zones.length === 1) {
        const next = new Set(this.collapsedAreas);
        if (next.has(k)) next.delete(k); else next.add(k);
        this.collapsedAreas = next;
        return;
      }
      const next = new Set(this.expandedAreas);
      if (next.has(k)) next.delete(k); else next.add(k);
      this.expandedAreas = next;
    },
    toggleZoneExpanded(windowKey, area, zone) {
      const k = this.zoneExpansionKey(windowKey, area.name, zone.name);
      if (zone.meters.length === 1) {
        const next = new Set(this.collapsedZones);
        if (next.has(k)) next.delete(k); else next.add(k);
        this.collapsedZones = next;
        return;
      }
      const next = new Set(this.expandedZones);
      if (next.has(k)) next.delete(k); else next.add(k);
      this.expandedZones = next;
    },
    zoneState(key, zone) {
      const set = this.selectedByWindow[key];
      if (!set || set.size === 0) return "none";
      const total = zone.meters.length;
      const selected = zone.meters.reduce((acc, m) => acc + (set.has(m.id) ? 1 : 0), 0);
      if (selected === 0) return "none";
      if (selected === total) return "all";
      return "some";
    },
    areaState(key, area) {
      const set = this.selectedByWindow[key];
      if (!set || set.size === 0) return "none";
      let total = 0;
      let selected = 0;
      for (const z of area.zones) {
        total += z.meters.length;
        for (const m of z.meters) if (set.has(m.id)) selected++;
      }
      if (selected === 0) return "none";
      if (selected === total) return "all";
      return "some";
    },
    toggleZone(key, zone, checked) {
      const next = { ...this.selectedByWindow };
      const current = next[key] ? new Set(next[key]) : new Set();
      for (const m of zone.meters) {
        if (checked) current.add(m.id); else current.delete(m.id);
      }
      if (current.size === 0) delete next[key]; else next[key] = current;
      this.selectedByWindow = next;
    },
    toggleArea(key, area, checked) {
      const next = { ...this.selectedByWindow };
      const current = next[key] ? new Set(next[key]) : new Set();
      for (const z of area.zones) {
        for (const m of z.meters) {
          if (checked) current.add(m.id); else current.delete(m.id);
        }
      }
      if (current.size === 0) delete next[key]; else next[key] = current;
      this.selectedByWindow = next;
    },
    groupTree(group) {
      const areas = new Map();
      for (const m of group.meters ?? []) {
        const areaName = m.area_name ?? "—";
        const zoneName = m.zone_name ?? "—";
        if (!areas.has(areaName)) areas.set(areaName, new Map());
        const zones = areas.get(areaName);
        if (!zones.has(zoneName)) zones.set(zoneName, []);
        zones.get(zoneName).push(m);
      }
      return Array.from(areas, ([name, zones]) => {
        const zoneList = Array.from(zones, ([zname, meters]) => ({
          name: zname,
          meters: [...meters].sort((a, b) => a.name.localeCompare(b.name)),
        })).sort((a, b) => a.name.localeCompare(b.name));
        const meterCount = zoneList.reduce((acc, z) => acc + z.meters.length, 0);
        return { name, zones: zoneList, meterCount };
      }).sort((a, b) => a.name.localeCompare(b.name));
    },
    clearSearch() {
      this.searchQuery = '';
      this.selectedByWindow = {};
    },
    isGroupExpanded(key) {
      if (this.allExpanded) return true;
      if (this.searchQuery.trim()) {
        return this.filteredGroups.some((g) => g.key === key);
      }
      return this.expandedGroup === key;
    },
    toggleAllExpanded() {
      if (this.allExpanded) {
        this.allExpanded = false;
        this.expandedGroup = null;
        this.expandedAreas = new Set();
        this.expandedZones = new Set();
        const collapsedAreas = new Set();
        const collapsedZones = new Set();
        for (const group of this.activeGroups) {
          const tree = this.groupTree(group);
          for (const area of tree) {
            if (area.zones.length === 1) collapsedAreas.add(this.areaExpansionKey(group.key, area.name));
            for (const zone of area.zones) {
              if (zone.meters.length === 1) collapsedZones.add(this.zoneExpansionKey(group.key, area.name, zone.name));
            }
          }
        }
        this.collapsedAreas = collapsedAreas;
        this.collapsedZones = collapsedZones;
      } else {
        this.allExpanded = true;
      }
    },
    toggleExpand(key) {
      if (this.allExpanded) {
        this.allExpanded = false;
        // If the clicked row was already open (any row is open when allExpanded), collapse it.
        // The user clicked to close this one specifically -- leave accordion empty.
        this.expandedGroup = null;
        return;
      }
      if (this.searchQuery.trim()) return;
      if (this.expandedGroup === key) {
        const next = { ...this.selectedByWindow };
        delete next[key];
        this.selectedByWindow = next;
        this.expandedGroup = null;
      } else {
        this.expandedGroup = key;
      }
    },
  },
};
</script>
